import {
  acceptCircuitToDistribute,
  CircuitParams,
  CountriesWithCircuits,
  createCircuit,
  createDistributorEditableCircuit,
  deleteCircuit,
  deleteDistributorEditableCircuit,
  deleteMultipleCircuits,
  DistributorCircuitsParams,
  DistributorCircuitsResponse,
  duplicateCircuit,
  DurationsWithCircuits,
  findDistributorCircuitCountries,
  findDistributorCircuitDurations,
  findDistributorCircuitReceivers,
  getCircuit,
  GetCircuitOptions,
  getCircuits,
  getCountriesWithCircuits,
  getDistributorCircuits,
  getDistributorEditableCircuit,
  getDraftsCircuits,
  getDurationsWithCircuits,
  getOriginalCircuitImages,
  getUndistributedCircuits,
  InvalidatedCircuitsParams,
  listDistributorEditableCircuits,
  ReceiversWithCircuits,
  updateCircuit,
  updateDistributorCircuit,
  UpdateDistributorCircuitDto,
  withdrawCircuit,
  getCircuitParagraphOverrides,
} from '@/api/circuits.api';
import { queryKeys } from '@/queryKeys';
import { SupportedLocale } from '@/types/supported-locale.type';
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

// Toutes les vues qui listent des circuits, y compris hors du préfixe
// 'circuits' : brouillons, filtre circuits des distributeurs et liste des
// circuits utilisant une prestation.
export const invalidateCircuitLists = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.circuits.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.drafts.circuits });
  queryClient.invalidateQueries({ queryKey: queryKeys.distributors.circuits });
  queryClient.invalidateQueries({ queryKey: queryKeys.benefits.circuitsBase });
  queryClient.invalidateQueries({ queryKey: queryKeys.trash.all });
};

export const useGetCircuits = (params: CircuitParams) => {
  return useQuery({
    queryKey: queryKeys.circuits.list(params),
    queryFn: () => getCircuits(params),
    placeholderData: keepPreviousData,
  });
};

export const useGetDistributorCircuits = <TData = DistributorCircuitsResponse>(
  params: DistributorCircuitsParams,
  select?: (data: DistributorCircuitsResponse) => TData,
) => {
  return useQuery({
    queryKey: queryKeys.circuits.distributors(params),
    queryFn: () => getDistributorCircuits(params),
    placeholderData: keepPreviousData,
    select,
  });
};

export const useGetUndistributedCircuits = (
  params: InvalidatedCircuitsParams,
) => {
  return useQuery({
    queryKey: queryKeys.circuits.distributorsInvalidated(params),
    queryFn: () => getUndistributedCircuits(params),
    placeholderData: keepPreviousData,
  });
};

export const useGetCountriesWithCircuits = <TData = CountriesWithCircuits>({
  onlyPublished = false,
  select,
}: {
  onlyPublished?: boolean;
  select?: (data: CountriesWithCircuits) => TData;
}) => {
  return useQuery({
    queryKey: queryKeys.circuits.countries({ onlyPublished }),
    queryFn: () => getCountriesWithCircuits(onlyPublished),
    placeholderData: keepPreviousData,
    select,
  });
};

export const useGetDurationsWithCircuits = <TData = DurationsWithCircuits>(
  select?: (data: DurationsWithCircuits) => TData,
) => {
  return useQuery({
    queryKey: queryKeys.circuits.durations,
    queryFn: () => getDurationsWithCircuits(),
    placeholderData: keepPreviousData,
    select,
  });
};

export const useDeleteCircuit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ circuitId }: { circuitId: string }) =>
      deleteCircuit(circuitId),
    onSuccess: () => {
      invalidateCircuitLists(queryClient);
    },
  });
};

export const useDeleteMultipleCircuits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ circuitIds }: { circuitIds: string[] }) =>
      deleteMultipleCircuits(circuitIds),
    onSuccess: () => {
      invalidateCircuitLists(queryClient);
    },
  });
};

export const useCreateCircuit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (circuit: FormData) => createCircuit(circuit),
    onSuccess: () => {
      invalidateCircuitLists(queryClient);
    },
  });
};

export const useUpdateCircuit = (language?: SupportedLocale) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      circuitId,
      circuit,
    }: {
      circuitId: string;
      circuit: FormData;
    }) => updateCircuit(circuitId, circuit, language),
    onSuccess: (_, { circuitId }) => {
      invalidateCircuitLists(queryClient);
      queryClient.invalidateQueries({
        queryKey: queryKeys.circuits.paragraphOverrides(circuitId),
      });
    },
  });
};

export const useGetDraftsCircuits = () =>
  useQuery({
    queryKey: queryKeys.drafts.circuits,
    queryFn: getDraftsCircuits,
  });

export const useGetCircuit = (
  id: string,
  originalImage?: boolean,
  shouldConvertToEuro?: boolean,
  enabled = true,
  options: GetCircuitOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.circuits.detail(id, {
      originalImage,
      shouldConvertToEuro,
      ...options,
    }),
    queryFn: () => getCircuit(id, originalImage, shouldConvertToEuro, options),
    enabled,
  });

export const useDuplicateCircuit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      circuitId,
      fields,
    }: {
      circuitId: string;
      fields: string[];
    }) => duplicateCircuit(circuitId, fields),
    onSuccess: () => {
      invalidateCircuitLists(queryClient);
    },
  });
};

export const useAcceptCircuitToDistribute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (circuitIds: string[]) => {
      return acceptCircuitToDistribute(circuitIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.distributors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.circuits.all });
    },
  });
};

export const useWithdrawCircuit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (circuitId: string) => {
      return withdrawCircuit(circuitId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.distributors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.circuits.all });
    },
  });
};

export const useGetDistributorCircuitReceivers = <
  TData = ReceiversWithCircuits,
>({
  isDistributed = true,
  select,
}: {
  isDistributed?: boolean;
  select?: (data: ReceiversWithCircuits) => TData;
}) => {
  return useQuery({
    queryKey: queryKeys.distributorCircuits.receivers({ isDistributed }),
    queryFn: () => findDistributorCircuitReceivers(isDistributed),
    placeholderData: keepPreviousData,
    select,
  });
};

export const useGetDistributorCircuitCountries = <
  TData = CountriesWithCircuits,
>({
  isDistributed = true,
  select,
}: {
  isDistributed?: boolean;
  select?: (data: CountriesWithCircuits) => TData;
}) => {
  return useQuery({
    queryKey: queryKeys.distributorCircuits.countries({ isDistributed }),
    queryFn: () => findDistributorCircuitCountries(isDistributed),
    placeholderData: keepPreviousData,
    select,
  });
};

export const useGetDistributorCircuitDurations = <
  TData = DurationsWithCircuits,
>({
  isDistributed = true,
  select,
}: {
  isDistributed?: boolean;
  select?: (data: DurationsWithCircuits) => TData;
}) => {
  return useQuery({
    queryKey: queryKeys.distributorCircuits.durations({ isDistributed }),
    queryFn: () => findDistributorCircuitDurations(isDistributed),
    placeholderData: keepPreviousData,
    select,
  });
};

export const useOriginalCircuitImages = (
  circuitId: string,
  accessToken?: string,
) => {
  return useQuery({
    queryKey: queryKeys.circuits.originalImages(circuitId, accessToken),
    queryFn: () => getOriginalCircuitImages(circuitId, accessToken),
    placeholderData: [],
  });
};

export const useListDistributorEditableCircuits = (
  params: {
    page: number;
    pageSize: number;
    sortField: string;
    sortOrder: 'asc' | 'desc';
    filters: Record<string, string>;
  },
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.circuits.editableCircuits(params),
    queryFn: () => listDistributorEditableCircuits(params),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useCreateDistributorEditableCircuit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (circuitId: string) =>
      createDistributorEditableCircuit(circuitId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.circuits.editableCircuitsBase,
      });
    },
  });
};

export const useGetDistributorEditableCircuit = (
  editableCircuitId: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: queryKeys.circuits.editableCircuitDetail(editableCircuitId),
    queryFn: () => getDistributorEditableCircuit(editableCircuitId),
    enabled,
  });
};

export const useUpdateDistributorCircuit = (editableCircuitId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateDistributorCircuitDto) =>
      updateDistributorCircuit(editableCircuitId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.circuits.editableCircuitsBase,
      });
    },
  });
};

export const useDeleteDistributorEditableCircuit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (editableCircuitId: string) =>
      deleteDistributorEditableCircuit(editableCircuitId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.circuits.editableCircuitsBase,
      });
    },
  });
};

export const useGetCircuitParagraphOverrides = (circuitId: string) =>
  useQuery({
    queryKey: queryKeys.circuits.paragraphOverrides(circuitId),
    queryFn: () => getCircuitParagraphOverrides(circuitId),
    enabled: !!circuitId,
  });
